// src/pages/EmployeeDetail.jsx
import React, { useEffect, useState, useMemo } from "react";
import Axios from "../utils/Axios";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Nav from "../components/Nav";
import "../styles/EmployeeDetail.css";
import Toast from "../utils/Toast";

import AuthHandler from "../utils/AuthHandler";
import validateForm, {
  StatusForTimesheet,
  StatusForTask,
} from "../utils/Validation";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "bootstrap/dist/css/bootstrap.min.css";

import { useDispatch, useSelector } from "react-redux";
import {
  DELETE_EMPLOYEE,
  EDIT_EMPLOYEE,
  EMPLOYEE_DETAILS_EMPID,
  LIST_ALL_TIMESHEETS,
} from "../config/API_Collection";
import jobTitlesData from "../config/jobTitles.json";

import { AddTimeEmployee } from "../components/AddTimeEmployee";
import TimeSheetApproval from "../components/TimesheetApproval";
import AccessRestricted from "../components/AccessRestricted";
import {
  GET_ALL_EMP_TASKS_REQUEST,
  GET_EMP_WEEKLY_TS_REQUEST,
} from "../store/Actions/Actions";
import { ROLE_LIST_1 } from "../config/API_Collection";

// Use the same dropdown component you shared before
import SearchableDropdown from "../components/DropdownSearch";

function FullPageLoaderLayout() {
  return (
    <div style={{ display: "flex", height: "100vh" }}>
      <Sidebar />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Nav />
        <div className="top-loading-bar" />
        <div className="employee-detail-container p-3">
          <div className="loading-wrapper">
            <div className="spinner-border text-secondary" role="status" />
          </div>
        </div>
       
      </div>
    </div>
  );
}

function RestrictedLayout({ homePath = "client" }) {
  return (
    <div style={{ display: "flex", height: "100vh" }}>
      <Sidebar />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Nav />
        <div className="h-100">
          <AccessRestricted homePath={homePath} />
        </div>
       
      </div>
    </div>
  );
}

export default function EmployeeDetail() {
  const navigate = useNavigate();
  const { id: empid } = useParams();
  const dispatch = useDispatch();

  const loginState = useSelector((s) => s?.login);
  const actions = loginState?.actions || null;
  const isBootstrapping = !!loginState?.isBootstrapping;

  const canViewEmployee = actions?.employees?.employee_details === true;
  const canEditEmployee = actions?.employees?.edit_employee === true;
  const canDeleteEmployee = actions?.employees?.delete_employee === true;

  const myTaskList = useSelector((s) => s?.allEmpTask?.myTasks) || [];
  const weekList = useSelector((s) => s?.empWeeklyTimesheets?.timesheets) || [];

  const [isLoading, setIsLoading] = useState(true);
  const [employeeData, setEmployeeData] = useState([]);
  // derive a profile image URL if the API returned one
  const derivedProfileImage =
    (employeeData && (
      employeeData.personal?.blob_path ||
      employeeData.profile_data?.blob_path ||
      employeeData.personal?.profile_image ||
      employeeData.personal?.photo
    )) || "/assets/images/default-profile.jpg";
  const [toast, setToast] = useState({ message: "", type: "", triggerId: 0 });
  const [formErrors, setFormErrors] = useState({});
  const [activeTab, setActiveTab] = useState("timesheets");

  const [showModalEdit, setShowModalEdit] = useState(false);
  const [showModalRemove, setShowModalRemove] = useState(false);

  const [showAddTime, setShowAddTime] = useState(false);
  const [timeSheetApro, setTimeSheetApro] = useState(false);
  const [taskAllocationModel, setTaskAllocationModel] = useState(false);
  const [weekProp, setWeekProp] = useState(null);
  const [roleLists, setRoleLists] = useState([]);

  const [allTSLoading, setAllTSLoading] = useState(false);
  const [allTSError, setAllTSError] = useState("");
  const [allEmployees, setAllEmployees] = useState([]);
  const [openEmpId, setOpenEmpId] = useState(null);
  const [expandedEmp, setExpandedEmp] = useState({});

  const [selectedEmpId, setSelectedEmpId] = useState(Number(empid));

  // lock Edit form on submit
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);

  const showToast = (message, type) =>
    setToast({ message, type, triggerId: Date.now() });

  useEffect(() => {
    if (empid && canViewEmployee) {
      fetchEmployeeDetails();
    }
    fetchJobRole();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empid, canViewEmployee]);

  useEffect(() => {
    if (canViewEmployee) {
      dispatch({ type: GET_ALL_EMP_TASKS_REQUEST, payload: empid });
      dispatch({ type: GET_EMP_WEEKLY_TS_REQUEST, payload: empid });
    }
  }, [dispatch, canViewEmployee, empid]);

  const fetchJobRole = async () => {
    try {
      const response = await Axios.get(ROLE_LIST_1);
      setRoleLists(response?.data);
    } catch (_) {}
  };

  const fetchEmployeeDetails = async () => {
    try {
      setIsLoading(true);
      const response = await Axios.get(`${EMPLOYEE_DETAILS_EMPID}${empid}`);
      if (response.status === 200) {
        setEmployeeData(response.data.data || []);
      }
    } catch (error) {
      AuthHandler(error.response?.status, setToast, navigate);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllTimesheet = async () => {
    setAllTSError("");
    try {
      setAllTSLoading(true);
      const res = await Axios.get(LIST_ALL_TIMESHEETS);
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      const mapped = list.map((e) => {
        const weeks = (e.timesheets || []).map((w) => {
          const weekStr = String(w.week || "");
          const weekNo = Number(weekStr.split("-")[1]) || null;
          const pct = Math.round((Number(w.progress) || 0) * 100);
          return {
            week_no: weekNo,
            week_date: weekStr,
            total_hours: w.hours ?? 0,
            progress: pct,
            status: w.status ?? null,
            timesheet_id: w.timesheet_id,
            is_current_week: false,
          };
        });
        return {
          employee_id: e.emp_id,
          name: e.emp_name,
          updated_at: e.last_updated_at,
          new_submissions: e.newly_submitted_count ?? 0,
          weeks,
        };
      });

      setAllEmployees(mapped);
    } catch (error) {
      setAllTSError("Failed to load all timesheets.");
      AuthHandler(error?.response?.status, setToast, navigate);
    } finally {
      setAllTSLoading(false);
    }
  };

  useEffect(() => {
    fetchAllTimesheet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openModalEdit = () => setShowModalEdit(true);
  const closeModalEdit = () => {
    setShowModalEdit(false);
    setIsSubmittingEdit(false);
  };

  const openModalRemove = () => setShowModalRemove(true);
  const closeModalRemove = () => setShowModalRemove(false);

  const handleRemoveEmployee = async () => {
    try {
      const payload = { employee_id: parseInt(empid, 10) };
      const response = await Axios.post(DELETE_EMPLOYEE, payload);
      if (response.status === 200) {
        showToast("Employee removed successfully! ", "success");
        closeModalRemove();
        setTimeout(() => navigate("/employees"), 3000);
      }
    } catch (error) {
      AuthHandler(error.response?.status, setToast, navigate);
    }
  };

  const resendActivation = () => {
    showToast("Resending activation email...", "info");
    dispatch({ type: "RESEND_ACTIVATION_REQUEST", payload: { employee_id: Number(empid) } });
  };

  // Show toast when saga updates resend status in store
  const employeeState = useSelector((s) => s?.employees || {});
  const [resendDone, setResendDone] = useState(false);
  useEffect(() => {
    if (employeeState?.resendMessage) {
      showToast(employeeState.resendMessage, "success");
      setResendDone(true);
    }
    if (employeeState?.resendError) {
      showToast(employeeState.resendError, "failed");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeState?.resendMessage, employeeState?.resendError]);

  // ---------- SearchableDropdown options + label/value mapping ----------
  const roleNameOptions = useMemo(
    () => (roleLists?.data || []).map((r) => r?.role_name).filter(Boolean),
    [roleLists]
  );
  const roleMapByName = useMemo(() => {
    const map = {};
    (roleLists?.data || []).forEach((r) => {
      if (r?.role_name != null) map[r.role_name] = String(r.role_id);
    });
    return map;
  }, [roleLists]);
  const roleMapById = useMemo(() => {
    const map = {};
    (roleLists?.data || []).forEach((r) => {
      if (r?.role_id != null) map[String(r.role_id)] = r.role_name;
    });
    return map;
  }, [roleLists]);

  const jobTitleOptions = useMemo(() => jobTitlesData.jobTitles, []);


  const billableStatusOptions = useMemo(() => ["Billable", "Non-Billable"], []);
  const officeOptions = useMemo(
    () => [
      "Hamilton (Head Office)",
      "Rotorua Office",
      "Thames Office",
      "Wellington Office",
      "Auckland Office",
      "Napier Office",
      "Whitianga Office",
      "Asia Office- Sri Lanka",
      "Whakatāne Office",
      "Cambridge",
      "Raglan",
      "Palmerston North",
      "Christchurch",
      "New Plymouth",
    ],
    []
  );

  // Dropdown label + hidden values (so backend payload stays the same)
  const [roleLabel, setRoleLabel] = useState("");
  const [roleIdHidden, setRoleIdHidden] = useState("");

  const [jobTitleLabel, setJobTitleLabel] = useState("");
  const [jobTitleHidden, setJobTitleHidden] = useState("");

  const [billableStatusLabel, setBillableStatusLabel] = useState("");
  const [billableStatusHidden, setBillableStatusHidden] = useState("");

  const [officeLabelModal, setOfficeLabelModal] = useState("");
  const [officeHidden, setOfficeHidden] = useState("");

  // Seed dropdowns when employee personal info or role list is available / modal opens
  useEffect(() => {
    if (!employeeData?.personal) return;

    const currentRoleId = employeeData.personal.role_id != null
      ? String(employeeData.personal.role_id)
      : "";
    const currentRoleLabel =
      roleMapById[currentRoleId] ||
      employeeData.personal.role_name ||
      "";
    setRoleLabel(currentRoleLabel);
    setRoleIdHidden(currentRoleId);

    const jt = employeeData.personal.job_title || "";
    setJobTitleLabel(jt);
    setJobTitleHidden(jt);


    const bs = employeeData.personal.billable || "";
    setBillableStatusLabel(bs);
    setBillableStatusHidden(bs);

    const off = employeeData.personal.office || "";
    setOfficeLabelModal(off);
    setOfficeHidden(off);
  }, [employeeData, roleMapById, showModalEdit]);

  // Dropdown change handlers
  const onPickRole = (label) => {
    setRoleLabel(label);
    setRoleIdHidden(roleMapByName[label] || "");
  };
  const onPickJobTitle = (label) => {
    setJobTitleLabel(label);
    setJobTitleHidden(label || "");
  };

  const onPickBillableStatus = (label) => {
    setBillableStatusLabel(label);
    setBillableStatusHidden(label || "");
  };

  const onPickOffice = (label) => {
    setOfficeLabelModal(label);
    setOfficeHidden(label || "");
  };

  const handleEditEmployee = async (e) => {
    console.log("Submitting edit for empid:", empid);
    e.preventDefault();
    if (isSubmittingEdit) return;

    const formData = e.target;
    try {
      
      const payload = {
        employee_id: parseInt(empid, 10),
        first_name: formData.first_name.value,
        last_name: formData.last_name.value,
        email: formData.email.value,
        phone: formData.phone.value,
        job_title: formData.job_title.value,
        role_id: parseInt(formData.role.value, 10),
        billable: formData.billable.value === "Billable" ? 1 : 2,
        billable_rate: formData.billable_rate.value,
        office: formData.office.value,
      };

      // persist profile image document id / url if available (best-effort)
      if (window && window.myProfileUploadData && window.myProfileUploadData.document_id) {
        payload.profile_document_id = window.myProfileUploadData.document_id;
      }

      const requiredFields = [
        "first_name",
        "last_name",
        "email",
        "phone",
        "job_title",
        "role_id",
        "billable",
        "billable_rate",
        "office",
      ];
      const errors = validateForm(payload, requiredFields);
      setFormErrors(errors);
      if (Object.keys(errors).length > 0) return;

      setIsSubmittingEdit(true);

      const response = await Axios.post(EDIT_EMPLOYEE, payload);
      if (response.status === 200) {
        showToast("Employee updated successfully!", "success");
        setIsSubmittingEdit(false);
        closeModalEdit();
        fetchEmployeeDetails();
        return;
      }

      showToast(response?.data?.message || "Failed to update employee", "failed");
      setIsSubmittingEdit(false);
    } catch (error) {
      AuthHandler(error.response?.status, setToast, navigate);
      setIsSubmittingEdit(false);
    }
  };

  const progressColor = (v) => {
    const p = Number(v?.progress || 0);
    if (p > 100) return "bg-danger";
    if (p >= 80) return "bg-success";
    if (p > 0) return "bg-warning";
    return "bg-info";
  };

  const allTimesheetEmployeesFallback = useMemo(() => {
    const fullName =
      `${employeeData?.personal?.first_name ?? ""} ${
        employeeData?.personal?.last_name ?? ""
      }`.trim() || "This Employee";

    return [
      {
        employee_id: Number(empid),
        name: fullName,
        updated_at:
          employeeData?.personal?.updated_at ||
          employeeData?.personal?.created_at ||
          "",
        new_submissions: 0,
        weeks: weekList || [],
      },
    ];
  }, [employeeData, empid, weekList]);

  if (isBootstrapping) return <FullPageLoaderLayout />;
  if (!canViewEmployee) return <RestrictedLayout homePath="client" />;

  return (
    <div style={{ display: "flex", height: "100vh" }}>
      <Sidebar />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Nav />
        {isLoading && <div className="top-loading-bar" />}
        <Toast message={toast.message} type={toast.type} triggerId={toast.triggerId} />

        <style>{`
          .tight-row > td, .tight-row > th { padding-top: 3px !important; padding-bottom: 3px !important; }
          .pointer { cursor: pointer; }
        `}</style>

        <div className="employee-detail-container p-3">
          <div className="row align-items-center">
            <div className="col-md-2 text-center">
              <img
                src={derivedProfileImage}
                alt="Profile"
                className="profile-pic-detail-view"
              />
            </div>
            <div className="col-md-6">
              <h2 className="employee-name">
                {employeeData.personal?.first_name} {employeeData.personal?.last_name}
                <span className="status-icon ms-2">
                  {employeeData.personal?.status === 1 && (
                    <span title="Active">
                      <i className="bi bi-patch-check text-success"></i> Active
                    </span>
                  )}
                  {employeeData.personal?.status === 0 && (
                    <span title="Pending" className="d-inline-flex align-items-center">
                      <i className="bi bi-hourglass"></i> Pending
                      <button
                        type="button"
                        className={`btn ${employeeState?.resendMessage || resendDone ? 'btn-outline-secondary' : 'btn-outline-primary'} btn-sm ms-2`}
                        onClick={resendActivation}
                        disabled={employeeState?.resendLoading || resendDone}
                      >
                        {employeeState?.resendLoading ? 'Sending...' : resendDone || employeeState?.resendMessage ? 'Activation Sent' : 'Resend Activation'}
                      </button>
                    </span>
                  )}
                  {employeeData.personal?.status === 3 && (
                    <span title="Inactive">
                      <i className="bi bi-ban text-danger"></i> Inactive
                    </span>
                  )}
                </span>
              </h2>
              <div className="mb-1 text-muted">
                {employeeData.personal?.role_name} {" - "}
                {employeeData.personal?.job_title}
              </div>
              <div className="text-smaller text-secondary">
                <div>Email: {employeeData.personal?.email}</div>
                <div>Phone: {employeeData.personal?.phone}</div>
              </div>
            </div>
            <div className="col-md-3 justify-content-end">
              <div className="text-smaller text-secondary">
                <div>
                  <i className="bi bi-calendar3"></i> Joining Date:{" "}
                  {employeeData.personal?.created_at}
                </div>
                <div className="mt-2">
                  <i className="bi bi-geo-alt-fill"></i> Location: {employeeData.personal?.office}
                </div>
              </div>
            </div>
            <div className="col-md-1 d-flex justify-content-end">
              <div className="dropdown">
                <button className="btn btn-sm" data-bs-toggle="dropdown" aria-expanded="false">
                  <i className="bi bi-three-dots-vertical"></i>
                </button>
                <ul className="dropdown-menu">
                  {canEditEmployee && (
                    <li onClick={openModalEdit}>
                      <a className="dropdown-item" href="#">Edit Employee</a>
                    </li>
                  )}
                  {canDeleteEmployee && (
                    <li onClick={openModalRemove}>
                      <a className="dropdown-item" href="#">Delete Employee</a>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>

          <div className="row align-items-center">
            <hr className="prf-sep-hr" />
          </div>

          <div className="tab-view-container p-3 pt-0">
            <ul className="nav nav-tabs">
              <li className="nav-item"></li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "timesheets" ? "active" : ""}`}
                  onClick={() => setActiveTab("timesheets")}
                >
                  Time Sheets
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "tasks" ? "active" : ""}`}
                  onClick={() => setActiveTab("tasks")}
                >
                  Task List
                </button>
              </li>
            </ul>

            <div className="tab-content border p-3">
              {showAddTime && (
                <AddTimeEmployee
                  setShowAddTime={setShowAddTime}
                  emp_id={selectedEmpId}
                  weekProp={weekProp}
                />
              )}

              {activeTab === "timesheets" && (
                <div>
                  <table className="table custom-table">
                    <thead className="table-light">
                      <tr>
                        <th>Week</th>
                        <th>Total Hours</th>
                        <th>Week Progress</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {weekList.length > 0 ? (
                        weekList.map((v, idx) => (
                          <tr
                            key={idx}
                            onClick={() => {
                              setSelectedEmpId(Number(empid));
                              setShowAddTime(true);
                              setWeekProp(v);
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            {v?.is_current_week ? (
                              <td className="text-primary">{`${v?.week_date} - This Week ${v?.week_no}`}</td>
                            ) : (
                              <td>{v?.week_date}</td>
                            )}
                            <td>{v?.total_hours} hrs</td>
                            <td>
                              <div className="progress" role="progressbar" aria-valuemin="0" aria-valuemax="100" style={{ height: 10 }}>
                                <div className={`progress-bar ${progressColor(v)}`} style={{ width: `${v?.progress || 0}%` }} />
                              </div>
                              <div className="small text-muted">{v?.progress}%</div>
                            </td>
                            <td><StatusForTimesheet code={v?.status} /></td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} className="text-center">No Data Available</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {timeSheetApro && (
                <TimeSheetApproval setTimeSheetApro={setTimeSheetApro} />
              )}

              {activeTab === "tasks" && (
                <div>
                  <table className="custom-table table">
                    <thead className="table-light">
                      <tr onClick={() => setTimeSheetApro(true)} style={{ cursor: "pointer" }}>
                        <th>ID</th>
                        <th>Task</th>
                        <th>Allocated Hours</th>
                        <th>Allocated Rate</th>
                        <th>Worked Hours</th>
                        <th>Completion</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {myTaskList.length > 0 ? (
                        myTaskList.map((v) => (
                          <tr key={v?.task_id}>
                            <td>T{v?.project_no}</td>
                            <td>
                              <div className="d-flex align-items-center">
                              {v?.project_name} - {v?.budget_name}
                              </div>
                              <div className="text-muted small">
                                {v?.task_name} - Assigned on {new Date(v?.assignment_date).toISOString().split('T')[0]}
                              </div>
                              </td>
                            <td>{v?.allocated_hours} hrs</td>
                            <td>${v?.allocated_rate}</td>
                            <td>{v?.worked_hours} hrs</td>
                            <td>
                              <div className="progress" role="progressbar" aria-valuemin="0" aria-valuemax="100" style={{ height: 10 }}>
                                <div className={`progress-bar ${progressColor(v)}`} style={{ width: `${v?.progress || 0}%` }} />
                              </div>
                              <div className="small text-muted">{v?.progress}%</div>
                            </td>
                            <td><StatusForTask code={v?.status} /></td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="text-center">No Data Available</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

         
        </div>

        {/* Edit Employee Modal */}
        {showModalEdit && canEditEmployee && (
          <div className="custom-project-modal">
            <div className="modal-backdrop fade show" />
            <div
              className="modal fade show"
              tabIndex="-1"
              aria-labelledby="exampleModalLabel"
              aria-hidden="true"
              style={{ display: "block", zIndex: 1050 }}
            >
              <div className="modal-dialog modal-lg mx-auto" style={{ marginTop: "2%" }}>
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title" id="exampleModalLabel">Edit Employee</h5>
                  </div>
                  <div className="modal-body">
                    <div className="add-budget-container">
                      <form className="budget-form" onSubmit={handleEditEmployee}>
                        {/* Lock inputs while submitting */}
                        <fieldset disabled={isSubmittingEdit} style={{ border: 0, padding: 0, margin: 0 }}>
                          <div className="form-row">
                            <div className={`form-group flex-item ${formErrors.first_name ? "has-error" : ""}`}>
                              <label>First Name *</label>
                              <input type="text" name="first_name" defaultValue={employeeData.personal?.first_name} />
                            </div>
                            <div className={`form-group flex-item ${formErrors.last_name ? "has-error" : ""}`}>
                              <label>Last Name *</label>
                              <input type="text" name="last_name" defaultValue={employeeData.personal?.last_name} />
                            </div>
                          </div>

                          <div className="form-row">
                            <div className={`form-group flex-item ${formErrors.email ? "has-error" : ""}`}>
                              <label>Email *</label>
                              <input type="email" name="email" defaultValue={employeeData.personal?.email} readOnly />
                            </div>
                            <div className={`form-group flex-item ${formErrors.phone ? "has-error" : ""}`}>
                              <label>Phone *</label>
                              <input
                                type="text"
                                name="phone"
                                defaultValue={employeeData.personal?.phone}
                                minLength={9}
                                maxLength={12}
                              />
                            </div>
                          </div>

                          <div className="form-row">
                            <div className={`form-group flex-item ${formErrors.role ? "has-error" : ""}`}>
                              <label>Job Role *</label>
                              <SearchableDropdown
                                label="Select Role"
                                allowAll={false}
                                options={roleNameOptions}
                                value={roleLabel}
                                onChange={onPickRole}
                              />
                              {/* backend expects role_id */}
                              <input type="hidden" name="role" value={roleIdHidden} />
                            </div>

                            <div className={`form-group flex-item ${formErrors.job_title ? "has-error" : ""}`}>
                              <label>Job Title *</label>
                              <SearchableDropdown
                                label="Select Job Title"
                                allowAll={false}
                                options={jobTitleOptions}
                                value={jobTitleLabel}
                                onChange={onPickJobTitle}
                              />
                              <input type="hidden" name="job_title" value={jobTitleHidden} />
                            </div>
                          </div>

                          <div className="form-row">
                            <div className={`form-group flex-item ${formErrors.billable ? "has-error" : ""}`}>
                              <label>Billable Status *</label>
                              <SearchableDropdown
                                label="Select Billable Status"
                                allowAll={false}
                                options={billableStatusOptions}
                                value={billableStatusLabel}
                                onChange={onPickBillableStatus}
                              />
                              <input type="hidden" name="billable" value={billableStatusHidden} />
                            </div>
                            <div className={`form-group flex-item ${formErrors.billable_rate ? "has-error" : ""}`}>
                              <label>Billable Rate *</label>
                              <input type="text" name="billable_rate" defaultValue={employeeData.personal?.billable_rate} />
                            </div>
                          </div>

                          <div className="form-row">
                            <div className="form-group flex-item">
                              <label>Location *</label>
                              <SearchableDropdown
                                label="Select Location"
                                allowAll={false}
                                options={officeOptions}
                                value={officeLabelModal}
                                onChange={onPickOffice}
                              />
                              <input type="hidden" name="office" value={officeHidden} />
                            </div>
                            <div className="form-group flex-item" />
                          </div>
                        </fieldset>

                        <div className="d-flex justify-content-center">
                          <button type="submit" className="btn btn-primary" disabled={isSubmittingEdit}>
                            {isSubmittingEdit ? "Updating..." : "Update Employee"}
                          </button>
                          <button type="button" className="btn btn-secondary ms-2" onClick={closeModalEdit}>
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Employee Modal */}
        {showModalRemove && canDeleteEmployee && (
          <div className="custom-project-modal">
            <div className="modal-backdrop fade show" />
            <div
              className="modal fade show"
              tabIndex="-1"
              aria-hidden="true"
              style={{ display: "block", zIndex: 1050 }}
            >
              <div className="modal-dialog  mx-auto" style={{ marginTop: "15%" }}>
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Confirm Employee Deletion</h5>
                  </div>
                  <div className="modal-body text-center">
                    <p>Are you sure you want to delete this employee?</p>
                  </div>
                  <div className="d-flex justify-content-center gap-5">
                    <button className="btn btn-danger px-5" onClick={handleRemoveEmployee}>
                      Yes
                    </button>
                    <button className="btn btn-secondary px-5" onClick={closeModalRemove}>
                      No
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
